import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MaterialModule } from '../../../shared/material/material.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { FormBuilder, Validators, FormsModule } from '@angular/forms';
import { MatTable } from '@angular/material/table';
import {
  AccessPlan,
  AccessPlanControllerService,
  AccessPlanFixedAmountDiscount,
  AccessPlanPercentageDiscount,
  Product,
  ProductPackage,
  ProductPackageControllerService,
  PaymentControllerService,
} from '../../../core/generated';
import { ProductControllerService } from '../../../core/generated/api/productController.service';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AdminPlanDialogComponent } from './admin-plan-dialog.component';
import { PlanDetailsDialogComponent } from './plan-details-dialog/plan-details-dialog.component';
import { PlanFullDetailsDialogComponent } from './plan-full-details-dialog/plan-full-details-dialog.component';

@Component({
  selector: 'app-admin-plan',
  standalone: true,
  imports: [MaterialModule, FormsModule],
  templateUrl: './admin-plan.component.html',
  styleUrl: './admin-plan.component.scss',
})
export class AdminPlanComponent implements OnInit {
  private _snackBar = inject(MatSnackBar);
  private cd = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private accessPlansService = inject(AccessPlanControllerService);
  private productPackagesService = inject(ProductPackageControllerService);
  private productsService = inject(ProductControllerService);
  private paymentsService = inject(PaymentControllerService);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  @ViewChild(MatTable) table!: MatTable<AccessPlan>;

  accessPlans: AccessPlan[] = [];
  products: Product[] = [];
  selectedPlan: any = null;
  selectedPlanTotal: number = 0;
  productPackages: ProductPackage[] = [];

  displayedColumns: string[] = [
    'id',
    'name',
    'cpfcnpj',
    'active',
    'items',
    'discounts',
    'createdAt',
    'actions',
  ];

  filterCpfCnpj: string = '';
  showOnlyActive: boolean = false;

  itemForm = this.fb.group({
    productId: [null as number | null, Validators.required],
    quantity: [1, [Validators.required, Validators.min(1)]],
    unitPrice: [0, [Validators.required, Validators.min(0)]],
  });

  discountForm = this.fb.group({
    type: ['PERCENTAGE' as 'PERCENTAGE' | 'FIXED_AMOUNT', Validators.required],
    description: ['', Validators.required],
    percentage: [0],
    amount: [0],
    discountAmount: [0],
  });

  applyPackageForm = this.fb.group({
    productPackageId: [null as number | null, Validators.required],
  });

  async ngOnInit() {
    await this.loadAllPlans();
    await this.loadProducts();
    await this.loadProductPackages();
  }

  async loadAllPlans() {
    try {
      const response = await firstValueFrom(
        this.accessPlansService.findAll11(0, 100, 'id')
      );

      if (response && response.content) {
        this.accessPlans = response.content;
      }

      this.cd.detectChanges();
      if (this.table) {
        this.table.renderRows();
      }
    } catch (error) {
      this._snackBar.open('Erro ao carregar planos', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro ao carregar planos:', error);
    }
  }

  async loadPlansByCpfCnpj(cpfCnpj: string) {
    const cleanCpfCnpj = cpfCnpj.replace(/\D/g, '');

    if (!cleanCpfCnpj || (cleanCpfCnpj.length !== 11 && cleanCpfCnpj.length !== 14)) {
      this._snackBar.open('CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos', 'Fechar', {
        duration: 3000,
      });
      return;
    }

    try {
      const response = await firstValueFrom(
        this.accessPlansService.findByCpfcnpj1(cleanCpfCnpj, 0, 100, 'id')
      );

      if (response && response.content) {
        this.accessPlans = response.content;
      }

      this.cd.detectChanges();
      if (this.table) {
        this.table.renderRows();
      }
    } catch (error) {
      this._snackBar.open('Erro ao filtrar planos por CPF/CNPJ', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro:', error);
    }
  }

  async loadProductPackages() {
    const response = await firstValueFrom(
      this.productPackagesService.findAll5(0, 100, 'id')
    );
    if (response && response.content) {
      this.productPackages = response.content as ProductPackage[];
    }
  }

  async loadActivePlansByCpfCnpj(cpfCnpj: string) {
    const cleanCpfCnpj = cpfCnpj.replace(/\D/g, '');

    if (!cleanCpfCnpj || (cleanCpfCnpj.length !== 11 && cleanCpfCnpj.length !== 14)) {
      this._snackBar.open('CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos', 'Fechar', {
        duration: 3000,
      });
      return;
    }

    try {
      this.accessPlans = await firstValueFrom(
        this.accessPlansService.findActivePlansByCpfcnpj(cleanCpfCnpj)
      );

      this.cd.detectChanges();
      if (this.table) {
        this.table.renderRows();
      }
    } catch (error) {
      this._snackBar.open('Erro ao carregar planos ativos', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro:', error);
    }
  }

  async loadPlanById(id: number) {
    try {
      this.selectedPlan = await firstValueFrom(
        this.accessPlansService.findById16(id)
      );
      this.selectedPlanTotal = await this.calculatePlanTotal(id);
    } catch (error) {
      this._snackBar.open('Erro ao carregar plano', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro:', error);
    }
  }

  async openPlanDetailsDialog(plan: AccessPlan) {
    if (!plan.id) return;

    try {
      const planTotal = await this.calculatePlanTotal(plan.id);

      this.dialog.open(PlanDetailsDialogComponent, {
        width: '900px',
        maxWidth: '95vw',
        data: {
          plan: plan,
          planTotal: planTotal,
        },
      });
    } catch (error) {
      this._snackBar.open('Erro ao abrir detalhes do plano', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro:', error);
    }
  }

  async openPlanFullDetailsDialog(planId: number) {
    try {
      await this.loadPlanById(planId);

      const dialogRef = this.dialog.open(PlanFullDetailsDialogComponent, {
        width: '1400px',
        maxWidth: '95vw',
        data: {
          plan: this.selectedPlan,
          planTotal: this.selectedPlanTotal,
          products: this.products,
          productPackages: this.productPackages,
          onAddItem: this.addItemToPlan.bind(this),
          onRemoveItem: this.removeItemFromPlan.bind(this),
          onAddDiscount: this.addDiscountToPlan.bind(this),
          onRemoveDiscount: this.removeDiscountFromPlan.bind(this),
          onApplyPackage: this.applyPackageToPlan.bind(this),
          onViewPayments: this.viewPayments.bind(this),
          onGeneratePayment: this.generatePayment.bind(this),
          onNavigateToInvoices: this.navigateToInvoices.bind(this),
        },
      });

      dialogRef.afterClosed().subscribe(() => {
        this.closePlanDetails();
        this.loadAllPlans();
      });
    } catch (error) {
      this._snackBar.open('Erro ao abrir detalhes completos do plano', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro:', error);
    }
  }

  openPlanDialog(plan?: AccessPlan) {
    const dialogRef = this.dialog.open(AdminPlanDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: { plan },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.savePlan(result);
      }
    });
  }

  async savePlan(plan: AccessPlan) {
    try {
      if (plan.id) {
        await firstValueFrom(this.accessPlansService.update17(plan.id, plan));
        this._snackBar.open('Plano atualizado com sucesso!', 'Fechar', {
          duration: 3000,
        });
      } else {
        await firstValueFrom(this.accessPlansService.save16(plan));
        this._snackBar.open('Plano criado com sucesso!', 'Fechar', {
          duration: 3000,
        });
      }

      await this.loadAllPlans();
    } catch (error) {
      this._snackBar.open('Erro ao salvar plano', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro ao salvar plano:', error);
    }
  }

  editPlan(plan: AccessPlan) {
    this.openPlanDialog(plan);
  }

  async deletePlan(id: number) {
    if (!confirm('Tem certeza que deseja excluir este plano?')) {
      return;
    }

    try {
      await firstValueFrom(this.accessPlansService.deleteById15(id));
      this._snackBar.open('Plano excluído com sucesso!', 'Fechar', {
        duration: 3000,
      });
      await this.loadAllPlans();
    } catch (error) {
      this._snackBar.open('Erro ao excluir plano', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro ao excluir plano:', error);
    }
  }

  async togglePlanStatus(plan: AccessPlan) {
    if (!plan.id) return;

    try {
      if (plan.active) {
        await firstValueFrom(this.accessPlansService.deactivatePlan(plan.id));
        this._snackBar.open('Plano desativado com sucesso!', 'Fechar', {
          duration: 3000,
        });
      } else {
        await firstValueFrom(this.accessPlansService.activatePlan(plan.id));
        this._snackBar.open('Plano ativado com sucesso!', 'Fechar', {
          duration: 3000,
        });
      }
      await this.loadAllPlans();
    } catch (error) {
      this._snackBar.open('Erro ao alterar status do plano', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro ao alterar status do plano:', error);
    }
  }

  async addItemToPlan(planId: number) {
    if (this.itemForm.invalid) {
      this._snackBar.open('Por favor, preencha todos os campos do item', 'Fechar', {
        duration: 3000,
      });
      return;
    }

    try {
      const itemCreate: any = {
        productId: this.itemForm.value.productId!,
        quantity: this.itemForm.value.quantity!,
        unitPrice: this.itemForm.value.unitPrice!,
      };

      await firstValueFrom(this.accessPlansService.addItem1(planId, itemCreate));

      this._snackBar.open('Item adicionado ao plano com sucesso!', 'Fechar', {
        duration: 3000,
      });

      this.resetItemForm();
      await this.loadPlanById(planId);
      await this.loadAllPlans();
    } catch (error) {
      this._snackBar.open('Erro ao adicionar item ao plano', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro:', error);
    }
  }

  async removeItemFromPlan(planId: number, itemId: number) {
    if (!confirm('Tem certeza que deseja remover este item?')) {
      return;
    }

    try {
      await firstValueFrom(this.accessPlansService.removeItem1(planId, itemId));

      this._snackBar.open('Item removido com sucesso!', 'Fechar', {
        duration: 3000,
      });

      await this.loadPlanById(planId);
      await this.loadAllPlans();
    } catch (error) {
      this._snackBar.open('Erro ao remover item', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro:', error);
    }
  }

  async addDiscountToPlan(planId: number) {
    if (this.discountForm.invalid) {
      this._snackBar.open('Por favor, preencha todos os campos do desconto', 'Fechar', {
        duration: 3000,
      });
      return;
    }

    try {
      const discountType = this.discountForm.value.type!;
      let discountInput: AccessPlanPercentageDiscount | AccessPlanFixedAmountDiscount;

      switch (discountType) {
        case 'PERCENTAGE':
          if (!this.discountForm.value.percentage) {
            this._snackBar.open('Por favor, informe a porcentagem do desconto', 'Fechar', {
              duration: 3000,
            });
            return;
          }
          discountInput = {
            type: 'PERCENTAGE',
            description: this.discountForm.value.description!,
            percentage: this.discountForm.value.percentage,
            active: true,
          } as AccessPlanPercentageDiscount;
          break;

        case 'FIXED_AMOUNT':
          if (!this.discountForm.value.amount) {
            this._snackBar.open('Por favor, informe o valor do desconto', 'Fechar', {
              duration: 3000,
            });
            return;
          }
          discountInput = {
            type: 'FIXED',
            description: this.discountForm.value.description!,
            amount: this.discountForm.value.amount,
            active: true,
          } as AccessPlanFixedAmountDiscount;
          break;

        default:
          this._snackBar.open('Tipo de desconto inválido', 'Fechar', {
            duration: 3000,
          });
          return;
      }

      await firstValueFrom(
        this.accessPlansService.addDiscount1(planId, discountInput as any)
      );

      this._snackBar.open('Desconto adicionado ao plano com sucesso!', 'Fechar', {
        duration: 3000,
      });

      this.resetDiscountForm();
      await this.loadPlanById(planId);
      await this.loadAllPlans();
    } catch (error) {
      this._snackBar.open('Erro ao adicionar desconto ao plano', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro:', error);
    }
  }

  async removeDiscountFromPlan(planId: number, discountId: number) {
    if (!confirm('Tem certeza que deseja remover este desconto?')) {
      return;
    }

    try {
      await firstValueFrom(
        this.accessPlansService.removeDiscount1(planId, discountId)
      );

      this._snackBar.open('Desconto removido com sucesso!', 'Fechar', {
        duration: 3000,
      });

      await this.loadPlanById(planId);
      await this.loadAllPlans();
    } catch (error) {
      this._snackBar.open('Erro ao remover desconto', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro ao remover desconto:', error);
    }
  }

  async calculatePlanTotal(planId: number): Promise<number> {
    try {
      const total = await firstValueFrom(
        this.accessPlansService.calculateTotal(planId)
      );
      return total;
    } catch (error) {
      console.error('Erro ao calcular total do plano:', error);
      return 0;
    }
  }

  async loadProducts() {
    try {
      this.products = await firstValueFrom(
        this.productsService.findAllActive()
      );
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  }

  clearFilters() {
    this.filterCpfCnpj = '';
    this.showOnlyActive = false;
    this.loadAllPlans();
  }

  applyFilters() {
    if (this.filterCpfCnpj) {
      if (this.showOnlyActive) {
        this.loadActivePlansByCpfCnpj(this.filterCpfCnpj);
      } else {
        this.loadPlansByCpfCnpj(this.filterCpfCnpj);
      }
    } else {
      this.loadAllPlans();
    }
  }

  resetPlanForm() {
    this.selectedPlan = null;
  }

  resetItemForm() {
    this.itemForm.reset({
      productId: null,
      quantity: 1,
      unitPrice: 0,
    });
  }

  resetDiscountForm() {
    this.discountForm.reset({
      type: 'PERCENTAGE',
      description: '',
      percentage: 0,
      amount: 0,
      discountAmount: 0,
    });
  }

  resetApplyPackageForm() {
    this.applyPackageForm.reset({ productPackageId: null });
  }

  closePlanDetails() {
    this.selectedPlan = null;
    this.selectedPlanTotal = 0;
  }

  formatCpfCnpj(cpfCnpj?: string): string {
    if (!cpfCnpj) return 'N/A';

    const numbers = cpfCnpj.replace(/\D/g, '');

    if (numbers.length === 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }

    if (numbers.length === 14) {
      return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }

    return cpfCnpj;
  }

  formatDate(date?: string): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('pt-BR');
  }

  formatPrice(price?: number): string {
    if (price === undefined || price === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  }

  getItemsCount(plan: AccessPlan): number {
    return plan.items?.length || 0;
  }

  getDiscountsCount(plan: AccessPlan): number {
    return plan.discounts?.length || 0;
  }

  getDiscountTypeLabel(type?: string): string {
    const typeMap: { [key: string]: string } = {
      PERCENTAGE: 'Porcentagem',
      FIXED_AMOUNT: 'Valor Fixo',
    };
    return type ? typeMap[type] || type : 'N/A';
  }

  getDiscountPercentage(discount: any): number | null {
    return discount.percentage !== undefined ? discount.percentage : null;
  }

  getDiscountAmount(discount: any): number | null {
    return discount.amount !== undefined ? discount.amount : null;
  }

  async applyPackageToPlan(planId: number) {
    if (this.applyPackageForm.invalid) {
      this._snackBar.open('Por favor, informe o ID do pacote de produtos', 'Fechar', {
        duration: 3000,
      });
      return;
    }

    const productPackageId = this.applyPackageForm.value.productPackageId!;

    if (!productPackageId) {
      this._snackBar.open('ID do pacote inválido', 'Fechar', { duration: 3000 });
      return;
    }

    if (!confirm('Tem certeza que deseja aplicar este pacote ao plano e gerar a fatura?')) {
      return;
    }

    try {
      await firstValueFrom(
        this.accessPlansService.applyPackageToAccessPlan(planId, productPackageId)
      );

      this._snackBar.open('Pacote aplicado e fatura gerada com sucesso!', 'Fechar', {
        duration: 3000,
      });

      this.resetApplyPackageForm();
      await this.loadPlanById(planId);
      await this.loadAllPlans();
    } catch (error) {
      this._snackBar.open('Erro ao aplicar pacote ao plano', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro ao aplicar pacote:', error);
    }
  }

  async applyPackageToSelectedPlan() {
    if (!this.selectedPlan || !this.selectedPlan.id) {
      this._snackBar.open('Nenhum plano selecionado', 'Fechar', { duration: 3000 });
      return;
    }

    await this.applyPackageToPlan(this.selectedPlan.id);
  }

  async viewPayments(planId: number) {
    if (!this.selectedPlan) return;
    const cpf = this.selectedPlan.cpfcnpj || this.selectedPlan.payerCnpj;
    if (cpf) {
      window.open(`/payments?cpfcnpj=${encodeURIComponent(cpf)}`, '_blank');
    } else {
      this._snackBar.open('CPF/CNPJ do plano não disponível para buscar pagamentos', 'Fechar', { duration: 3000 });
    }
  }

  async generatePayment(planId: number) {
    if (!confirm('Gerar pagamento para este plano?')) return;

    try {
      const createReq: any = {
        planId: planId,
        numberOfInstallments: 1,
        method: 'PIX',
      };

      await firstValueFrom(this.paymentsService.createPayment(createReq));
      this._snackBar.open('Pagamento gerado com sucesso!', 'Fechar', { duration: 3000 });
      await this.loadPlanById(planId);
      await this.loadAllPlans();
    } catch (error) {
      console.error('Erro ao gerar pagamento:', error);
      this._snackBar.open('Erro ao gerar pagamento', 'Fechar', { duration: 5000 });
    }
  }

  navigateToInvoices(planId: number) {
    window.open(`/invoices?planId=${planId}`, '_blank');
  }
}
